// ============================================================
// Vanderclix Collar Builder — Core Logic v2
// ============================================================

(function () {
  'use strict';

  // ----------------------------------------------------------
  // 2. SNAP POSITIONS — per size (x%, y% of preview container)
  // All images are 2400×800px (3:1 ratio).
  // y% is measured from top of the image.
  // Positions are fine-tunable via ?config=1 drag interface.
  // ----------------------------------------------------------
  const DEFAULT_SNAP_POSITIONS = {
    S: [
      { x: 40.4, y: 49.4  },
      { x: 48.2, y: 49.1  },
      { x: 55.41, y: 49.41 },
    ],
    M: [
      { x: 37.3, y: 48.9 },
      { x: 42.2, y: 48.9 },
      { x: 47.3, y: 48.9 },
      { x: 52.3, y: 48.8 },
      { x: 57.5, y: 49.2 },
    ],
    L: [
      { x: 35.2, y: 49.7 },
      { x: 42.2, y: 49.9 },
      { x: 49.0, y: 50.1 },
      { x: 55.9, y: 49.8 },
      { x: 62.9, y: 50.3 },
    ],
  };

  const SIZES = ['S', 'M', 'L'];
  const SNAP_COUNT = { S: 3, M: 5, L: 5 };

  // ----------------------------------------------------------
  // 4. STATE
  // ----------------------------------------------------------
  function loadSavedPositions() {
    try {
      const saved = localStorage.getItem('vc_snap_positions_v2');
      return saved ? JSON.parse(saved) : null;
    } catch(e) { return null; }
  }

  // Product, price, variant, category, order, and preview data now come from
  // Shopify products and their Custom data fields rendered by the section.
  const rawCatalog = window.VCBuilder && window.VCBuilder.catalog;
  const rawCopy = (window.VCBuilder && window.VCBuilder.copy) || {};
  const COPY = {
    position: 'Position [number]',
    pickCharm: 'Pick a charm below',
    remove: 'Remove',
    snap: 'Snap [number]',
    selectAll: 'Select all [total] charms ([filled]/[total])',
    addToCart: '✦ Add to Cart — [price]',
    adding: 'Adding…',
    added: 'Added to cart!',
    viewCart: 'View Cart',
    ...rawCopy,
  };
  if (!rawCatalog || !rawCatalog.base || !Array.isArray(rawCatalog.charms)) {
    console.error('VC Builder catalog is missing. Check the section product and collection settings.');
    return;
  }

  const COLLAR_URLS = {};
  Object.entries(rawCatalog.base.previewImages || {}).forEach(([color, sizes]) => {
    Object.entries(sizes || {}).forEach(([size, url]) => {
      COLLAR_URLS[`${color} ${size}`] = url;
    });
  });

  const CHARM_URLS = {};
  const SNAP_VARIANT_MAP = {};
  const CHARMS = {};
  const tabOrder = Array.isArray(rawCatalog.base.tabOrder) ? rawCatalog.base.tabOrder : [];
  const charmsByCategory = rawCatalog.charms.reduce((groups, charm) => {
    if (!charm || !charm.handle || !charm.name || !charm.category || !charm.variantId) return groups;

    const normalized = {
      id: charm.handle,
      name: charm.name,
      priceCents: Number(charm.priceCents),
      sortOrder: Number(charm.sortOrder) || 0,
      available: charm.available !== false,
    };
    normalized.price = normalized.priceCents / 100;
    CHARM_URLS[normalized.id] = charm.image || '';
    SNAP_VARIANT_MAP[normalized.id] = Number(charm.variantId);
    (groups[charm.category] || (groups[charm.category] = [])).push(normalized);
    return groups;
  }, {});

  [...tabOrder, ...Object.keys(charmsByCategory).filter((category) => !tabOrder.includes(category)).sort()].forEach((category) => {
    const charms = charmsByCategory[category];
    if (!charms || !charms.length) return;
    CHARMS[category] = charms.sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name));
  });

  const COLORS = rawCatalog.base.variants.map((variant) => {
    const label = variant.title;
    return {
      id: label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      label,
      hex: (rawCatalog.base.colorHex || {})[label] || '#2C2118',
      variantId: Number(variant.id),
      priceCents: Number(variant.priceCents),
      available: variant.available !== false,
    };
  });

  if (!COLORS.length || !Object.keys(CHARMS).length) {
    console.error('VC Builder catalog has no base variants or charms.');
    return;
  }

  const defaultColor = COLORS.find((color) => color.available) || COLORS[0];
  const state = {
    color: defaultColor.id,
    size: 'S',
    activeSnap: 0,
    snaps: [null, null, null],   // S default = 3 snaps; resized on size change
    snapPositions: loadSavedPositions() || JSON.parse(JSON.stringify(DEFAULT_SNAP_POSITIONS)),
    activeTab: Object.keys(CHARMS)[0],
    configMode: false,
  };

  let dom = {};
  let isAdding = false;

  // ----------------------------------------------------------
  // 5. HELPERS
  // ----------------------------------------------------------
  function responsiveShopifyImageUrl(url, width) {
    if (!url || !Number.isFinite(width)) return url || '';

    try {
      const parsedUrl = new URL(url, window.location.origin);
      if (parsedUrl.hostname !== 'cdn.shopify.com') return url;
      parsedUrl.searchParams.set('width', String(Math.round(width)));
      return parsedUrl.toString();
    } catch (error) {
      return url;
    }
  }

  function collarSrc() {
    const label = COLORS.find(c => c.id === state.color).label;
    const width = window.matchMedia('(max-width: 749px)').matches ? 1200 : 1800;
    return responsiveShopifyImageUrl(COLLAR_URLS[`${label} ${state.size}`], width);
  }

  function charmSrc(charm) {
    return charm ? responsiveShopifyImageUrl(CHARM_URLS[charm.id], 320) : '';
  }

  function currentColor() {
    return COLORS.find((color) => color.id === state.color);
  }

  function makeKeyboardActivatable(element, activate) {
    element.setAttribute('role', 'button');
    element.tabIndex = 0;
    element.addEventListener('click', activate);
    element.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      activate();
    });
  }

  function totalPriceCents() {
    const basePriceCents = currentColor()?.priceCents || 0;
    return basePriceCents + state.snaps.reduce((sum, charm) => sum + (charm ? charm.priceCents : 0), 0);
  }

  function totalPrice() {
    return totalPriceCents() / 100;
  }

  function fmt(n) { return 'US$' + n.toFixed(2); }

  function copyText(key, replacements = {}) {
    let value = String(COPY[key] || '');
    Object.entries(replacements).forEach(([name, replacement]) => {
      value = value.split(`[${name}]`).join(String(replacement));
    });
    return value;
  }

  const analyticsState = {
    started: false,
    selectionComplete: false,
  };

  function selectedCharmCount() {
    return state.snaps.filter(Boolean).length;
  }

  function analyticsParams(extra = {}) {
    return {
      builder_color: currentColor()?.label || state.color,
      builder_size: state.size,
      charm_count: selectedCharmCount(),
      required_count: SNAP_COUNT[state.size],
      ...extra,
    };
  }

  function trackEvent(name, params = {}) {
    if (typeof window.gtag !== 'function') return;
    try {
      window.gtag('event', name, params);
    } catch (error) {
      console.debug('VC Builder analytics event skipped:', name, error);
    }
  }

  function markBuilderStarted() {
    if (analyticsState.started) return;
    analyticsState.started = true;
    trackEvent('diy_builder_started', analyticsParams());
  }

  function syncSelectionCompletion() {
    const isComplete = state.snaps.length === SNAP_COUNT[state.size] && state.snaps.every(Boolean);
    if (isComplete && !analyticsState.selectionComplete) {
      trackEvent('diy_selection_completed', analyticsParams({
        value: totalPrice(),
        currency: 'USD',
      }));
    }
    analyticsState.selectionComplete = isComplete;
  }

  function currentPositions() {
    return state.snapPositions[state.size];
  }

  // Vanderclix: decimal-level color-specific fine-tune for Size S, Snap 2 only.
  // Display-only; does not mutate base positions or config-mode drag calibration.
  const S_SNAP2_COLOR_ADJUST = {
    // All values are percentage points on the collar preview coordinate system.
    obsidian: { dx: -0.1,  dy: 0    },  // black (unchanged)
    saddle:   { dx: -0.05, dy: 0.04 },  // brown: -0.2+0.15=-0.05
    sage:     { dx: -0.02, dy: 0    },  // mint: -0.15+0.13=-0.02
    sakura:   { dx: -0.30, dy: 0.33 },  // pink: -0.45+0.15=-0.30
    tidal:    { dx: -0.1,  dy: 0.1  },  // blue (unchanged)
  };

  // Vanderclix: Snap 1 color-specific fine-tune for Size S (display-only).
  const S_SNAP1_COLOR_ADJUST = {
    obsidian: { dx: 0.27, dy: 0 },  // black: 0.18+0.09=0.27%
    saddle:   { dx: 0.15, dy: 0 },  // brown: right 0.15%
    sage:     { dx: 0.13, dy: 0 },  // mint: right 0.13%
    sakura:   { dx: 0.05, dy: 0 },  // pink: right 0.05%
    tidal:    { dx: 0.1,  dy: 0 },  // blue: right 0.1%
  };

  // Vanderclix: Snap 3 color-specific fine-tune for Size S (display-only).
  const S_SNAP3_COLOR_ADJUST = {
    saddle: { dx: 0.15, dy: -0.01 },  // brown: right 0.15%
    sage:   { dx: 0.13, dy: 0     },  // mint: right 0.13%
    sakura: { dx: 0.10, dy: 0     },  // pink: right 0.10%
  };


  // Vanderclix: L码各颜色孔位精细校准 (display-only)
  const L_COLOR_ADJUST = {
    obsidian: [  // 黑色L码
      { dx: 0,     dy: 0     },
      { dx: 0,     dy: 0     },
      { dx: 0,     dy: -0.15 },  // 3号孔 上0.15%
      { dx: 0,     dy: 0     },
      { dx: 0,     dy: -0.35 },  // 5号孔再上移0.15%
    ],
    saddle: [    // 棕色L码
      { dx: -0.08, dy: 0.65  },  // 1号孔再上移0.13%
      { dx: 0,     dy: 0.62  },  // 2号孔再上移0.13%
      { dx: 0,     dy: 0.55  },  // 3号孔再上移0.20%
      { dx: 0,     dy: 0.76  },  // 4号孔再上移0.13%
      { dx: 0,     dy: 0.46  },  // 5号孔再上移0.23%
    ],
    sakura: [    // 粉色L码
      { dx: 0,     dy: 0     },
      { dx: 0,     dy: 0     },
      { dx: 0,     dy: 0     },
      { dx: 0,     dy: 0.08  },  // 4号孔 下0.08%
      { dx: -0.07, dy: -0.14 },  // 5号孔 左0.07%，上0.14%
    ],
    sage: [      // 薄荷绿L码
      { dx: 0,     dy: -0.16 },  // 1号孔 上0.16%
      { dx: 0,     dy: -0.10 },  // 2号孔 上0.10%
      { dx: 0,     dy: -0.15 },  // 3号孔 上0.15%
      { dx: 0,     dy: 0.06  },  // 4号孔 下0.06%
      { dx: 0,     dy: -0.15 },  // 5号孔 上0.15%
    ],
    tidal: [     // 蓝色L码
      { dx: 0,     dy: 0.23  },  // 1号孔再下移0.13%
      { dx: 0,     dy: 0     },
      { dx: 0,     dy: -0.09 },  // 3号孔 上0.09%
      { dx: 0,     dy: 0.09  },  // 4号孔 下0.09%
      { dx: 0,     dy: -0.38 },  // 5号孔 在原位置上移0.12%
    ],
  };

  // Vanderclix: M码各颜色孔位精细校准 (display-only)
  // 孔位索引 0=1号孔 ... 4=5号孔; dx负=左, dy负=上
  const M_COLOR_ADJUST = {
    obsidian: [  // 黑色
      { dx: 0,      dy: 0      },
      { dx: 0,      dy: 0      },
      { dx: 0,      dy: 0      },
      { dx: 0,      dy: 0      },
      { dx: 0,      dy: -0.01  },  // 5号孔 上0.01%
    ],
    saddle: [    // 棕色
      { dx: -0.27,  dy: 0.23   },  // 1号孔 左0.27% 下0.23%
      { dx: -0.05,  dy: 0.23   },  // 2号孔 左0.05% 下0.23%
      { dx: -0.05,  dy: 0.23   },  // 3号孔 左0.05% 下0.23%
      { dx:  0.30,  dy: 0.23   },  // 4号孔 右0.30% 下0.23%
      { dx: -0.16,  dy: -0.01  },  // 5号孔 左0.16% 上0.01%
    ],
    sakura: [    // 粉色
      { dx: -0.32,  dy: 0      },  // 1号孔 左0.32%
      { dx: -0.29,  dy: 0      },  // 2号孔 左0.29%
      { dx: -0.28,  dy: 0      },  // 3号孔 左0.28%
      { dx: -0.1108,dy: 0      },  // 4号孔 左0.1108%
      { dx: -0.1108,dy: 0      },  // 5号孔 左0.1108%
    ],
    sage: [      // 薄荷绿
      { dx: -0.20,  dy: 0      },  // 1号孔 左0.20%
      { dx: 0,      dy: 0      },
      { dx: -0.007, dy: 0      },  // 3号孔 左0.007%
      { dx: -0.007, dy: 0.1    },  // 4号孔 左0.007% 下0.1%
      { dx: -0.20,  dy: 0      },  // 5号孔 左0.20%
    ],
    tidal: [     // 蓝色
      { dx: -0.27,  dy: 0      },  // 1号孔 左0.27%
      { dx: -0.107, dy: 0      },  // 2号孔 左0.107%
      { dx: -0.104, dy: 0      },  // 3号孔 左0.104%
      { dx: 0,      dy: 0      },
      { dx: 0,      dy: -0.072 },  // 5号孔 上0.072%
    ],
  };

  function displayPositions() {
    const base = currentPositions();
    if (state.size === 'M') {
      const mAdj = M_COLOR_ADJUST[state.color];
      if (!mAdj) return base;
      return base.map((pos, idx) => {
        const a = mAdj[idx];
        return a ? { x: pos.x + a.dx, y: pos.y + a.dy } : pos;
      });
    }
    if (state.size === 'L') {
      const lAdj = L_COLOR_ADJUST[state.color];
      if (!lAdj) return base;
      return base.map((pos, idx) => {
        const a = lAdj[idx];
        return a ? { x: pos.x + a.dx, y: pos.y + a.dy } : pos;
      });
    }
    if (state.size !== 'S') return base;
    return base.map((pos, idx) => {
      if (idx === 0) {
        const a1 = S_SNAP1_COLOR_ADJUST[state.color];
        return a1 ? { x: pos.x + a1.dx, y: pos.y + a1.dy } : pos;
      }
      if (idx === 1) {
        const a2 = S_SNAP2_COLOR_ADJUST[state.color];
        return a2 ? { x: pos.x + a2.dx, y: pos.y + a2.dy } : pos;
      }
      if (idx === 2) {
        const a3 = S_SNAP3_COLOR_ADJUST[state.color];
        return a3 ? { x: pos.x + a3.dx, y: pos.y + a3.dy } : pos;
      }
      return pos;
    });
  }

  // ----------------------------------------------------------
  // 6. RENDER — Snap overlays (clickable circles, Caroline-Crow style)
  // Each snap is:
  //   • Empty (inactive) → small grey hollow circle
  //   • Empty (active)   → gold filled circle with pulse halo
  //   • Filled           → charm image floating above (NO circle behind)
  //                        Click filled charm → re-selects that slot
  // ----------------------------------------------------------
  function renderSnapPins() {
    dom.snapContainer.innerHTML = '';

    displayPositions().forEach((pos, i) => {
      const wrap = document.createElement('div');
      wrap.className = 'vc-snap' +
        (i === state.activeSnap ? ' is-active' : '') +
        (state.snaps[i]         ? ' is-filled'  : '');
      wrap.style.left = pos.x + '%';
      wrap.style.top  = pos.y + '%';
      wrap.dataset.snap = i;
      wrap.title = state.snaps[i]
        ? `Position ${i + 1}: ${state.snaps[i].name} — click to change`
        : `Position ${i + 1} — click to select`;

      if (state.snaps[i]) {
        // Filled: show charm image, no dot
        const img = document.createElement('img');
        img.src       = charmSrc(state.snaps[i]);
        img.alt       = state.snaps[i].name;
        img.decoding  = 'async';
        // Four oversized charms keep original size; all others are scaled down
        const LARGE_CHARMS = ['Aqua Butterfly','Turquoise Garden','Pastel Bouquet','Cobalt Flower'];
        const isLargeCharm = LARGE_CHARMS.includes(state.snaps[i].name);
        img.className = 'vc-snap-charm' +
          (isLargeCharm ? ' vc-snap-charm--large' : '');
        wrap.dataset.charmSize = isLargeCharm ? 'large' : 'normal';

        // Vanderclix: per-charm fine-tune (scale + vertical/horizontal offset + rotation), applied via CSS vars
        const CHARM_ADJUST = {
          'Crystal Halo':     { scale: 0.644,  dy: -5,  dx: 0,  rotate: 0 },
          'Frost Halo':       { scale: 0.7830, dy: -3,  dx: 0,  rotate: 0 },
          'Violet Halo':      { scale: 0.7256, dy: -11, dx: 0,  rotate: 0 },
          'Crimson Halo':     { scale: 0.6503, dy: -9,  dx: 0,  rotate: 0 },
          'Berry Radiance':   { scale: 0.90,   dy: -5,  dx: 0,  rotate: 0 },
          'Rosé Radiance':    { scale: 0.86,   dy: 0,   dx: 0,  rotate: 0 },
          'Emerald Grande':   { scale: 0.9016, dy: -6,  dx: 0,  rotate: 0 },
          'Emerald Petite':   { scale: 0.9312, dy: -7,  dx: 0,  rotate: 0 },
          'Forest Royale':    { scale: 0.7954, dy: -6,  dx: 0,  rotate: 0 },
          'Pink Pavé':        { scale: 0.6324, dy: -5,  dx: 0,  rotate: 0 },
          'Forest Gleam':     { scale: 0.784,  dy: -3,  dx: 0,  rotate: 0 },
          'Blush Drop':       { scale: 0.77,   dy: -7,  dx: 0,  rotate: 0 },
          'Blush Halo':       { scale: 0.768,  dy: -1,  dx: 0,  rotate: 0.001 },
          'Ruby Prism':       { scale: 0.80,   dy: 0,   dx: 0,  rotate: 3 },
          'Amber Radiance':   { scale: 0.80,   dy: 3,   dx: 0,  rotate: 0 },
          'Crystal Regent':   { scale: 0.80,   dy: -3,  dx: 0,  rotate: 0 },
          'Violet Clover':    { scale: 0.75,   dy: -3,  dx: 0,  rotate: 0 },
          'Rose Clover':      { scale: 0.7862, dy: -3,  dx: -4, rotate: 0 },
          'Aqua Butterfly':   { scale: 1.00,   dy: 0,   dx: 0,  rotate: 0 },
          'Ivory Halo':       { scale: 0.64,   dy: -6,  dx: 0,  rotate: 0 },
          'Lilac Halo':       { scale: 0.7905, dy: -5,  dx: 0,  rotate: 0 },
          'Ruby Ember':       { scale: 0.864,  dy: -3,  dx: 0,  rotate: 0 },
          'Forest Oval':      { scale: 0.864,  dy: -5,  dx: 0,  rotate: 0 },
          'Aurora Jewel':     { scale: 0.8439, dy: -7,  dx: 0,  rotate: 0 },
          'Silver Bloom':     { scale: 0.8004, dy: -10, dx: 0,  rotate: 0 },
          'Golden Bloom':     { scale: 0.8017, dy: -7,  dx: 0,  rotate: 0 }
        };
        const adjust = CHARM_ADJUST[state.snaps[i].name];
        if (adjust) {
          img.style.setProperty('--vc-cs', adjust.scale);
          img.style.setProperty('--vc-cy', adjust.dy + '%');
          if (adjust.dx) img.style.setProperty('--vc-cx', adjust.dx + '%');
          if (adjust.rotate) img.style.setProperty('--vc-cr', adjust.rotate + 'deg');
        }
        wrap.appendChild(img);
      } else {
        // Empty: hollow circle (grey) or gold filled (active)
        const dot = document.createElement('div');
        dot.className = 'vc-snap-dot';
        wrap.appendChild(dot);
      }

      // Position number label (small, top-right of dot)
      const num = document.createElement('div');
      num.className   = 'vc-snap-num';
      num.textContent = (i + 1).toString();
      wrap.appendChild(num);

      makeKeyboardActivatable(wrap, () => onSnapClick(i));

      // 手机端：数字标签独立可点击，阻止冒泡避免触发两次
      num.addEventListener('click', (e) => { e.stopPropagation(); onSnapClick(i); });

      if (state.configMode) makeDraggable(wrap, i);

      dom.snapContainer.appendChild(wrap);
    });
  }

  // ----------------------------------------------------------
  // 7. RENDER — Charm Grid
  // ----------------------------------------------------------
  function renderCharmGrid() {
    dom.charmGrid.innerHTML = '';
    CHARMS[state.activeTab].forEach(charm => {
      const cell = document.createElement('div');
      const selected = state.snaps[state.activeSnap] &&
                       state.snaps[state.activeSnap].id === charm.id;
      cell.className = 'vc-charm-cell' + (selected ? ' is-selected' : '') + (charm.available ? '' : ' is-unavailable');
      cell.title = charm.available
        ? (selected ? `${charm.name} — click again to remove` : `Add ${charm.name} to position ${state.activeSnap + 1}`)
        : `${charm.name} is currently unavailable`;
      if (!charm.available) {
        cell.setAttribute('aria-disabled', 'true');
        cell.tabIndex = -1;
      }

      const img = document.createElement('img');
      img.src     = charmSrc(charm);
      img.alt     = charm.name;
      img.loading = 'lazy';
      img.decoding = 'async';

      const lbl = document.createElement('span');
      lbl.textContent = charm.name;

      cell.appendChild(img);
      cell.appendChild(lbl);

      if (charm.price > 0) {
        const pr = document.createElement('span');
        pr.className  = 'vc-charm-price';
        pr.textContent = '+' + fmt(charm.price);
        cell.appendChild(pr);
      }

      if (charm.available) makeKeyboardActivatable(cell, () => onCharmSelect(charm));
      dom.charmGrid.appendChild(cell);
    });
  }

  // ----------------------------------------------------------
  // 7b. RENDER — Active slot editor strip (above charm grid)
  // Shows: "Position N — [Charm name]  [Remove]"  OR  "Position N — empty"
  // ----------------------------------------------------------
  function renderSlotEditor() {
    if (!dom.slotEditor) return;
    const i = state.activeSnap;
    const charm = state.snaps[i];

    dom.slotEditor.innerHTML = '';
    const bar = document.createElement('div');
    bar.className = 'vc-slot-editor-bar' + (charm ? ' is-filled' : '');

    const label = document.createElement('div');
    label.className = 'vc-slot-editor-label';
    const position = document.createElement('span');
    position.className = 'vc-slot-pos';
    position.textContent = copyText('position', { number: i + 1 });
    const name = document.createElement('span');
    name.className = 'vc-slot-name' + (charm ? '' : ' vc-slot-empty');
    name.textContent = charm ? charm.name : COPY.pickCharm;
    label.append(position, name);
    bar.appendChild(label);

    if (charm) {
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'vc-slot-remove-btn';
      removeBtn.textContent = COPY.remove;
      removeBtn.addEventListener('click', onSlotRemove);
      bar.appendChild(removeBtn);
    }

    dom.slotEditor.appendChild(bar);
  }

  // ----------------------------------------------------------
  // 8. RENDER — Summary bar
  // ----------------------------------------------------------
  function renderSummary() {
    dom.priceDisplay.textContent = fmt(totalPrice());

    dom.snapSummary.innerHTML = '';
    state.snaps.forEach((charm, i) => {
      const badge = document.createElement('div');
      badge.className = 'vc-summary-badge' + (charm ? ' is-filled' : ' is-empty');
      badge.textContent = charm ? charm.name : copyText('snap', { number: i + 1 });
      dom.snapSummary.appendChild(badge);
    });

    const total  = SNAP_COUNT[state.size];
    const filled = state.snaps.filter(Boolean).length;
    const allDone = filled === total;
    dom.addToCartBtn.disabled = isAdding || !allDone;
    dom.addToCartBtn.textContent = isAdding
      ? COPY.adding
      : allDone
        ? copyText('addToCart', { price: fmt(totalPrice()) })
        : copyText('selectAll', { filled, total });
  }

  // ----------------------------------------------------------
  // 9. FULL RENDER
  // ----------------------------------------------------------
  function render() {
    // Collar image
    const newSrc = collarSrc();
    if (dom.collarImg.src !== newSrc) dom.collarImg.src = newSrc;

    // Color swatches
    dom.colorSwatches.querySelectorAll('.vc-color-swatch').forEach(el =>
      el.classList.toggle('is-active', el.dataset.color === state.color)
    );

    // Size buttons
    dom.sizeButtons.querySelectorAll('.vc-size-btn').forEach(el =>
      el.classList.toggle('is-active', el.dataset.size === state.size)
    );

    // Tabs
    dom.tabs.querySelectorAll('.vc-tab').forEach(el =>
      el.classList.toggle('is-active', el.dataset.tab === state.activeTab)
    );

    renderSnapPins();
    renderCharmGrid();
    renderSlotEditor();
    renderSummary();
  }

  // ----------------------------------------------------------
  // 10. EVENT HANDLERS
  // ----------------------------------------------------------
  function onSnapClick(i) {
    markBuilderStarted();
    state.activeSnap = i;
    render();
    // If the slot already has a charm, scroll the charm grid into view
    // so user can see the [Remove / Replace] options on the right
    if (state.snaps[i] && dom.charmGrid) {
      const editor = document.getElementById('vc-slot-editor');
      if (editor) editor.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  function onCharmSelect(charm) {
    markBuilderStarted();
    const selectedSlot = state.activeSnap;
    const current = state.snaps[state.activeSnap];
    // Click the same charm again → remove it (toggle)
    if (current && current.id === charm.id) {
      state.snaps[state.activeSnap] = null;
      render();
      trackEvent('diy_charm_removed', analyticsParams({
        charm_id: charm.id,
        slot_number: selectedSlot + 1,
      }));
      syncSelectionCompletion();
      return;
    }
    state.snaps[state.activeSnap] = charm;
    // Auto-advance to next empty snap (only if this slot was previously empty)
    if (!current) {
      const next = state.snaps.findIndex((s, idx) => idx > state.activeSnap && s === null);
      if (next !== -1) state.activeSnap = next;
    }
    render();
    trackEvent('diy_charm_selected', analyticsParams({
      charm_id: charm.id,
      slot_number: selectedSlot + 1,
    }));
    syncSelectionCompletion();
  }

  function onSlotRemove() {
    if (state.snaps[state.activeSnap]) {
      markBuilderStarted();
      const removedCharm = state.snaps[state.activeSnap];
      const removedSlot = state.activeSnap;
      state.snaps[state.activeSnap] = null;
      render();
      trackEvent('diy_charm_removed', analyticsParams({
        charm_id: removedCharm.id,
        slot_number: removedSlot + 1,
      }));
      syncSelectionCompletion();
    }
  }

  // ----------------------------------------------------------
  // 11. DRAG (Config Mode)
  // ----------------------------------------------------------
  function makeDraggable(el, snapIdx) {
    let dragging = false, ox, oy, startL, startT;
    el.style.cursor = 'grab';

    el.addEventListener('mousedown', e => {
      dragging = true;
      ox = e.clientX; oy = e.clientY;
      startL = currentPositions()[snapIdx].x;
      startT = currentPositions()[snapIdx].y;
      e.preventDefault();
      e.stopPropagation();
    });
    document.addEventListener('mousemove', e => {
      if (!dragging) return;
      const rect = dom.previewArea.getBoundingClientRect();
      const dx = ((e.clientX - ox) / rect.width)  * 100;
      const dy = ((e.clientY - oy) / rect.height) * 100;
      currentPositions()[snapIdx].x = Math.min(96, Math.max(4, startL + dx));
      currentPositions()[snapIdx].y = Math.min(96, Math.max(4, startT + dy));
      renderSnapPins();
    });
    document.addEventListener('mouseup', () => {
      if (dragging) {
        dragging = false;
        localStorage.setItem('vc_snap_positions_v2', JSON.stringify(state.snapPositions));
      }
    });
  }

  // ----------------------------------------------------------
  // 12. ADD TO CART
  // ----------------------------------------------------------
  function createSetId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `set_${crypto.randomUUID()}`;
    }
    return `set_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  function buildCartRequest() {
    const color = currentColor();
    const requiredSnapCount = SNAP_COUNT[state.size];
    if (!color || !color.available || !Number.isSafeInteger(color.variantId)) {
      throw new Error('The selected collar variant is unavailable.');
    }
    if (state.snaps.length !== requiredSnapCount || state.snaps.some((charm) => !charm)) {
      throw new Error('Select every required charm before adding the set.');
    }

    const setId = createSetId();
    const collarProps = {
      'Collar Color': color.label,
      'Size': state.size,
    };

    const snapCounts = new Map();
    state.snaps.forEach((charm, index) => {
      if (!charm.available || !Number.isSafeInteger(charm.priceCents)) {
        throw new Error(`The charm in position ${index + 1} is unavailable.`);
      }
      const variantId = SNAP_VARIANT_MAP[charm.id];
      if (!Number.isSafeInteger(variantId)) {
        throw new Error(`The charm in position ${index + 1} has no purchasable variant.`);
      }
      collarProps[`Snap ${index + 1}`] = charm.name;
      const existing = snapCounts.get(charm.id);
      snapCounts.set(charm.id, existing ? { charm, quantity: existing.quantity + 1 } : { charm, quantity: 1 });
    });

    const snapTotalCents = Array.from(snapCounts.values()).reduce(
      (total, { charm, quantity }) => total + charm.priceCents * quantity,
      0
    );
    const setTotalCents = color.priceCents + snapTotalCents;
    if (!Number.isSafeInteger(setTotalCents) || setTotalCents <= 0) {
      throw new Error('The set price could not be calculated.');
    }

    // 项圈隐藏属性（_ 开头顾客不可见）
    collarProps['_set_id'] = setId;
    collarProps['_set_total'] = String(setTotalCents);
    collarProps['_snap_summary'] = Array.from(snapCounts.values())
      .map(({ charm, quantity }) => (quantity > 1 ? `${charm.name} \u00d7${quantity}` : charm.name))
      .join(', ');

    const items = [
      { id: color.variantId, quantity: 1, properties: collarProps }
    ];
    snapCounts.forEach(({ charm, quantity }) => {
      items.push({ id: SNAP_VARIANT_MAP[charm.id], quantity, properties: { '_set_id': setId } });
    });

    return { items };
  }

  function hideCartError({ restoreFocus = false } = {}) {
    if (!dom.cartError) return;
    dom.cartError.hidden = true;
    if (restoreFocus && dom.addToCartBtn) dom.addToCartBtn.focus();
  }

  function showCartError() {
    if (!dom.cartError) return;
    dom.cartError.hidden = false;
    dom.cartError.focus({ preventScroll: true });
  }

  function setAddingState(active) {
    isAdding = active;
    if (dom.cartRetryBtn) dom.cartRetryBtn.disabled = active;
    renderSummary();
  }

  async function addToCart() {
    if (isAdding) return;
    try {
      hideCartError();
      const requestBody = buildCartRequest();
      markBuilderStarted();
      trackEvent('diy_add_to_cart_attempted', analyticsParams({
        value: totalPrice(),
        currency: 'USD',
      }));
      setAddingState(true);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      if (!res.ok) throw new Error('Cart add failed: ' + res.status);

      // 加购成功 —— 不整页跳转（慢且费请求），只更新右上角数量 + 显示轻量提示，
      // 顾客可继续配下一套；想结账点提示里的"View Cart"
      try {
        const cart = await (await fetch('/cart.js')).json();
        document.querySelectorAll('.cart-count-bubble span[aria-hidden], .cart-count-bubble span:first-child')
          .forEach(function(el) { el.textContent = cart.item_count; });
        document.querySelectorAll('.cart-count-bubble').forEach(function(b) { b.style.display = ''; });
      } catch (e) { /* 数量更新失败不影响加购结果 */ }

      showAddedToast();
      trackEvent('diy_set_added_to_cart', analyticsParams({
        value: totalPrice(),
        currency: 'USD',
      }));
      setAddingState(false);
    } catch (err) {
      console.error('addToCart error:', err);
      markBuilderStarted();
      trackEvent('diy_set_add_failed', analyticsParams({
        failure_type: err instanceof TypeError ? 'network' : 'cart_or_validation',
      }));
      setAddingState(false);
      showCartError();
    }
  }

  // 加购成功浮层提示
  function showAddedToast() {
    var old = document.getElementById('vc-added-toast');
    if (old) old.remove();
    var t = document.createElement('div');
    t.id = 'vc-added-toast';
    t.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);z-index:99999;background:#2d2a26;color:#fff;padding:14px 22px;border-radius:8px;box-shadow:0 6px 24px rgba(0,0,0,0.25);font-size:15px;display:flex;align-items:center;gap:16px;animation:vcToastIn .3s ease;';
    var message = document.createElement('span');
    message.textContent = `✓ ${COPY.added}`;
    var cartLink = document.createElement('a');
    cartLink.href = '/cart';
    cartLink.style.cssText = 'color:#fff;text-decoration:underline;font-weight:600;white-space:nowrap;';
    cartLink.textContent = `${COPY.viewCart} →`;
    t.append(message, cartLink);
    document.body.appendChild(t);
    if (!document.getElementById('vc-toast-style')) {
      var st = document.createElement('style');
      st.id = 'vc-toast-style';
      st.textContent = '@keyframes vcToastIn{from{opacity:0;transform:translateX(-50%) translateY(-10px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}';
      document.head.appendChild(st);
    }
    setTimeout(function() { if (t.parentNode) { t.style.transition='opacity .4s'; t.style.opacity='0'; setTimeout(function(){t.remove();},400); } }, 3500);
  }

  // ----------------------------------------------------------
  // 13. CONFIG MODE  (?config=1)
  // ----------------------------------------------------------
  function initConfigMode() {
    if (new URLSearchParams(window.location.search).get('config') !== '1') return;
    state.configMode = true;

    const banner = document.createElement('div');
    banner.className = 'vc-config-banner';
    banner.innerHTML = `
      <strong>⚙ Config Mode</strong> — Drag snap dots to the correct snap position on each size.
      Switch sizes with the S/M/L buttons above to calibrate each independently.
      <button id="vc-reset-pos">Reset all to default</button>
      <small style="opacity:.6">Positions auto-saved in browser localStorage.</small>
    `;
    document.querySelector('.vc-builder-wrap').prepend(banner);
    banner.querySelector('#vc-reset-pos').addEventListener('click', () => {
      state.snapPositions = JSON.parse(JSON.stringify(DEFAULT_SNAP_POSITIONS));
      localStorage.removeItem('vc_snap_positions_v2');
      renderSnapPins();
    });
  }

  // ----------------------------------------------------------
  // 14. INIT
  // ----------------------------------------------------------
  function init() {
    dom = {
      collarImg:    document.getElementById('vc-collar-img'),
      previewArea:  document.getElementById('vc-preview-area'),
      snapContainer:document.getElementById('vc-snap-container'),
      colorSwatches:document.getElementById('vc-color-swatches'),
      sizeButtons:  document.getElementById('vc-size-buttons'),
      tabs:         document.getElementById('vc-tabs'),
      charmGrid:    document.getElementById('vc-charm-grid'),
      slotEditor:   document.getElementById('vc-slot-editor'),
      priceDisplay: document.getElementById('vc-price'),
      snapSummary:  document.getElementById('vc-snap-summary'),
      addToCartBtn: document.getElementById('vc-add-to-cart'),
      cartError:    document.getElementById('vc-cart-error'),
      cartRetryBtn: document.getElementById('vc-cart-retry'),
      cartDismissBtn: document.getElementById('vc-cart-error-dismiss'),
    };
    if (!dom.collarImg) return;

    // Color swatches
    COLORS.forEach(c => {
      const btn = document.createElement('button');
      btn.className = 'vc-color-swatch';
      btn.dataset.color = c.id;
      btn.title = c.available ? c.label : `${c.label} is currently unavailable`;
      btn.style.background = c.hex;
      btn.setAttribute('aria-label', c.label);
      btn.disabled = !c.available;
      btn.addEventListener('click', () => {
        if (state.color === c.id) return;
        markBuilderStarted();
        state.color = c.id;
        render();
        trackEvent('diy_option_selected', analyticsParams({
          option_type: 'color',
          option_value: c.label,
        }));
      });
      dom.colorSwatches.appendChild(btn);
    });

    // Size buttons
    SIZES.forEach(s => {
      const btn = document.createElement('button');
      btn.className = 'vc-size-btn';
      btn.dataset.size = s;
      btn.textContent = s;
      btn.addEventListener('click', () => {
        if (state.size === s) return;
        markBuilderStarted();
        const newCount = SNAP_COUNT[s];
        const oldCount = SNAP_COUNT[state.size];
        state.size = s;
        if (newCount !== oldCount) {
          // Resize snaps array; trim or extend with nulls
          state.snaps = Array.from({ length: newCount }, (_, i) => state.snaps[i] || null);
          state.activeSnap = Math.min(state.activeSnap, newCount - 1);
        }
        render();
        trackEvent('diy_option_selected', analyticsParams({
          option_type: 'size',
          option_value: s,
        }));
        syncSelectionCompletion();
      });
      dom.sizeButtons.appendChild(btn);
    });

    // Tab buttons
    Object.keys(CHARMS).forEach(tab => {
      const btn = document.createElement('button');
      btn.className = 'vc-tab';
      btn.dataset.tab = tab;
      btn.textContent = tab;
      btn.addEventListener('click', () => { state.activeTab = tab; render(); });
      dom.tabs.appendChild(btn);
    });

    dom.addToCartBtn.addEventListener('click', addToCart);
    if (dom.cartRetryBtn) dom.cartRetryBtn.addEventListener('click', addToCart);
    if (dom.cartDismissBtn) {
      dom.cartDismissBtn.addEventListener('click', () => hideCartError({ restoreFocus: true }));
    }
    initConfigMode();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
